import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import {
  getItemExplanation,
  normalizeConfidenceScore,
  enrichPriceEstimateFields,
} from './utils/formatters';
import {
  DISALLOWED_SITE_MESSAGE,
  DEFAULT_SCAN_DELAY_MS,
  isAllowedAuctionUrl,
  clampScanDelayMs,
  dedupeImageUrls,
  tryFetchPageHtmlForScan,
} from './utils/analyzeWebsiteHelpers';
import { useRuntimeSettings } from './context/RuntimeSettingsContext';
import AnalyzeWebsiteControls from './components/analyze-website/AnalyzeWebsiteControls';
import DetectedImagesList from './components/analyze-website/DetectedImagesList';
import TextSnippetsList from './components/analyze-website/TextSnippetsList';
import ManifestPanel from './components/analyze-website/ManifestPanel';
import AnalyzeWebsiteResults from './components/analyze-website/AnalyzeWebsiteResults';

const AnalyzeWebsitePage = () => {
  const {
    modelName,
    setModelName,
    resizeMode,
    setResizeMode,
    compressionMode,
    setCompressionMode,
    useEbayRag,
    setUseEbayRag,
    useNotesRag,
    setUseNotesRag,
    assumeHiddenItems,
    setAssumeHiddenItems,
  } = useRuntimeSettings();

  const [websiteUrl, setWebsiteUrl] = useState('');
  const [loadingScan, setLoadingScan] = useState(false);
  const [loadingAnalyze, setLoadingAnalyze] = useState(false);
  const [scanError, setScanError] = useState('');
  const [analyzeError, setAnalyzeError] = useState('');
  const [scannedUrl, setScannedUrl] = useState('');
  const [scanMeta, setScanMeta] = useState(null);
  const [images, setImages] = useState([]);
  const [textSnippets, setTextSnippets] = useState([]);
  const [selectedTexts, setSelectedTexts] = useState([]);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [scanDelayMs, setScanDelayMs] = useState(DEFAULT_SCAN_DELAY_MS);
  const [result, setResult] = useState(null);
  const [originalPrices, setOriginalPrices] = useState({});
  const [originalNames, setOriginalNames] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingNameId, setUpdatingNameId] = useState(null);

  const allSelected = useMemo(
    () => images.length > 0 && selectedUrls.length === images.length,
    [images.length, selectedUrls.length],
  );

  const toggleSelected = (url) => {
    setSelectedUrls((prev) => (
      prev.includes(url) ? prev.filter((x) => x !== url) : [...prev, url]
    ));
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUrls([]);
    } else {
      setSelectedUrls(dedupeImageUrls(images.map((item) => item.url)));
    }
  };

  const allTextsSelected = useMemo(
    () => textSnippets.length > 0 && selectedTexts.length === textSnippets.length,
    [textSnippets.length, selectedTexts.length],
  );
  const selectedImageUrls = useMemo(
    () => dedupeImageUrls(selectedUrls),
    [selectedUrls],
  );
  const manifestRows = useMemo(
    () => (
      Array.isArray(textSnippets)
        ? textSnippets.filter((row) => row && (row.source === 'manifest_csv' || row.source === 'manifest_html'))
        : []
    ),
    [textSnippets],
  );
  const manifestInfo = (scanMeta && typeof scanMeta.manifest === 'object' && scanMeta.manifest) ? scanMeta.manifest : null;
  const hasManifestCandidates = Boolean(Array.isArray(manifestInfo?.candidates) && manifestInfo.candidates.length > 0);

  const toggleSelectedText = (textValue) => {
    setSelectedTexts((prev) => (
      prev.includes(textValue) ? prev.filter((x) => x !== textValue) : [...prev, textValue]
    ));
  };

  const toggleSelectAllTexts = () => {
    if (allTextsSelected) {
      setSelectedTexts([]);
    } else {
      setSelectedTexts(textSnippets.map((item) => item.text));
    }
  };

  const handleScan = async () => {
    const url = String(websiteUrl || '').trim();
    if (!url) return;
    if (!isAllowedAuctionUrl(url)) {
      setScanError(DISALLOWED_SITE_MESSAGE);
      return;
    }
    setLoadingScan(true);
    setScanError('');
    setAnalyzeError('');
    setResult(null);
    setScanMeta(null);
    setTextSnippets([]);
    setSelectedTexts([]);
    setSelectedUrls([]);

    try {
      const effectiveScanDelayMs = clampScanDelayMs(scanDelayMs);
      const pageHtml = await tryFetchPageHtmlForScan(url);
      const scanPayload = {
        url,
        model: modelName,
        scan_delay_ms: effectiveScanDelayMs,
      };
      if (pageHtml) {
        scanPayload.page_html = pageHtml;
      } else {
        // No client-side HTML (CORS-blocked on most auction sites).
        // Request the backend to render the page in a headless browser so
        // that JS-injected content (image carousels, lazy-loaded media,
        // etc.) is captured — this mirrors what the Chrome extension sees
        // via the live DOM and keeps the "Analyze Website" image count
        // in sync with the extension.
        scanPayload.force_browser_scan = true;
      }
      const response = await axios.post('/scan_website_images', scanPayload);
      const nextImages = Array.isArray(response?.data?.images) ? response.data.images : [];
      setImages(nextImages);
      setSelectedUrls(
        dedupeImageUrls(
          nextImages
            .filter((item) => item?.selected !== false)
            .map((item) => item.url),
        ),
      );
      const nextTextSnippets = Array.isArray(response?.data?.text_snippets) ? response.data.text_snippets : [];
      setTextSnippets(nextTextSnippets);
      setSelectedTexts(
        nextTextSnippets
          .filter((item) => item?.selected !== false)
          .map((item) => item.text),
      );
      setScannedUrl(response?.data?.url || url);
      setScanMeta({
        selectedCount: Number(response?.data?.selected_count || 0),
        duplicateCount: Number(response?.data?.duplicate_count || 0),
        relevanceAppliedCount: Number(response?.data?.relevance_applied_count || 0),
        textRelevanceAppliedCount: Number(response?.data?.text_relevance_applied_count || 0),
        relevanceModel: response?.data?.relevance_model || modelName,
        selectedTextCount: Number(response?.data?.selected_text_count || 0),
        textCount: Number(response?.data?.text_count || 0),
        scanDelayMs: Number(response?.data?.scan_delay_ms ?? effectiveScanDelayMs),
        manifest: response?.data?.manifest && typeof response.data.manifest === 'object' ? response.data.manifest : null,
      });
    } catch (error) {
      setScanError(error?.response?.data?.message || error?.message || 'Failed to scan website.');
      setImages([]);
      setSelectedUrls([]);
      setTextSnippets([]);
      setSelectedTexts([]);
      setScannedUrl('');
      setScanMeta(null);
    } finally {
      setLoadingScan(false);
    }
  };

  const handleAnalyze = async () => {
    const imageUrls = dedupeImageUrls(selectedUrls);
    if (imageUrls.length === 0) return;
    setLoadingAnalyze(true);
    setAnalyzeError('');
    setResult(null);
    try {
      const response = await axios.post('/analyze', {
        image_urls: imageUrls,
        website_context_text: selectedTexts,
        model: modelName,
        resize_mode: resizeMode,
        compression_mode: compressionMode,
        use_ebay_rag: useEbayRag,
        use_notes_rag: useNotesRag,
        assume_hidden_items: assumeHiddenItems,
      });
      const resultData = response?.data || {};
      resultData.items = (Array.isArray(resultData.items) ? resultData.items : []).map((item) => {
        if (!item || typeof item !== 'object') return item;
        const explanation = getItemExplanation(item);
        const enrichedItem = enrichPriceEstimateFields(item);
        return {
          ...enrichedItem,
          explanation,
          margin_analysis: explanation,
          confidence_score: normalizeConfidenceScore(
            item.confidence_score ?? item.confidence ?? item.confidenceScore
          ),
        };
      });
      setResult(resultData);

      const prices = {};
      const names = {};
      (resultData.items || []).forEach((item) => {
        if (item?.id) {
          prices[item.id] = item.actual_price;
          names[item.id] = item.manual_name || '';
        }
      });
      setOriginalPrices(prices);
      setOriginalNames(names);
    } catch (error) {
      setAnalyzeError(error?.response?.data?.message || error?.message || 'Analyze failed.');
    } finally {
      setLoadingAnalyze(false);
    }
  };

  const handlePriceChange = (id, value) => {
    setResult((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => (item.id === id ? { ...item, actual_price: value } : item)),
      };
    });
  };

  const handleManualNameChange = (id, value) => {
    setResult((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => (item.id === id ? { ...item, manual_name: value } : item)),
      };
    });
  };

  const updatePrice = async (id, price) => {
    setUpdatingId(id);
    try {
      await axios.post('/update_price', { id, actual_price: parseFloat(price) });
      setOriginalPrices((prev) => ({ ...prev, [id]: parseFloat(price) }));
    } catch (error) {
      alert(error?.response?.data?.message || 'Failed to save price.');
    } finally {
      setUpdatingId(null);
    }
  };

  const updateItemName = async (id, name) => {
    setUpdatingNameId(id);
    try {
      await axios.post('/update_item_name', { id, item_name: name });
      setOriginalNames((prev) => ({ ...prev, [id]: name }));
    } catch (error) {
      alert(error?.response?.data?.message || 'Failed to save item name.');
    } finally {
      setUpdatingNameId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">Analyse web-site</h1>

      <AnalyzeWebsiteControls
        websiteUrl={websiteUrl}
        setWebsiteUrl={setWebsiteUrl}
        loadingScan={loadingScan}
        onScan={handleScan}
        modelName={modelName}
        setModelName={setModelName}
        resizeMode={resizeMode}
        setResizeMode={setResizeMode}
        compressionMode={compressionMode}
        setCompressionMode={setCompressionMode}
        scanDelayMs={scanDelayMs}
        setScanDelayMs={setScanDelayMs}
        useEbayRag={useEbayRag}
        setUseEbayRag={setUseEbayRag}
        useNotesRag={useNotesRag}
        setUseNotesRag={setUseNotesRag}
        assumeHiddenItems={assumeHiddenItems}
        setAssumeHiddenItems={setAssumeHiddenItems}
      />

      {scanError ? <div className="mb-3 text-sm text-red-600">{scanError}</div> : null}
      {scannedUrl ? <div className="mb-3 text-xs text-slate-500 break-all">Scanned: {scannedUrl}</div> : null}
      {scanMeta ? (
        <div className="mb-3 text-xs text-slate-600">
          Auto-selected {scanMeta.selectedCount}/{images.length} | Duplicates: {scanMeta.duplicateCount} | LLM relevance checked: {scanMeta.relevanceAppliedCount} ({scanMeta.relevanceModel})
          {` | Text relevance checked: ${scanMeta.textRelevanceAppliedCount} | Text selected: ${scanMeta.selectedTextCount}/${scanMeta.textCount} | Delay: ${scanMeta.scanDelayMs}ms`}
        </div>
      ) : null}

      <DetectedImagesList
        images={images}
        selectedUrls={selectedUrls}
        allSelected={allSelected}
        onToggleSelected={toggleSelected}
        onToggleSelectAll={toggleSelectAll}
      />

      <TextSnippetsList
        textSnippets={textSnippets}
        selectedTexts={selectedTexts}
        allTextsSelected={allTextsSelected}
        onToggleSelectedText={toggleSelectedText}
        onToggleSelectAllTexts={toggleSelectAllTexts}
      />

      <ManifestPanel
        scanMeta={scanMeta}
        manifestInfo={manifestInfo}
        manifestRows={manifestRows}
        hasManifestCandidates={hasManifestCandidates}
      />

      <button
        type="button"
        onClick={handleAnalyze}
        disabled={loadingAnalyze || selectedImageUrls.length === 0}
        className={`w-full py-3 rounded-lg text-white font-semibold ${loadingAnalyze || selectedImageUrls.length === 0 ? 'bg-slate-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
      >
        {loadingAnalyze ? <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" />Estimating...</span> : 'Estimate'}
      </button>

      {analyzeError ? <div className="mt-3 text-sm text-red-600">{analyzeError}</div> : null}

      {result ? (
        <AnalyzeWebsiteResults
          result={result}
          modelName={modelName}
          resizeMode={resizeMode}
          compressionMode={compressionMode}
          useEbayRag={useEbayRag}
          useNotesRag={useNotesRag}
          assumeHiddenItems={assumeHiddenItems}
          originalPrices={originalPrices}
          originalNames={originalNames}
          updatingId={updatingId}
          updatingNameId={updatingNameId}
          onPriceChange={handlePriceChange}
          onManualNameChange={handleManualNameChange}
          onSavePrice={updatePrice}
          onSaveItemName={updateItemName}
        />
      ) : null}
    </div>
  );
};

export default AnalyzeWebsitePage;
